// ==================== PRIVACY POLICY PAGE ====================

import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Server, Users, Mail, FileText, AlertCircle } from 'lucide-react';
import { fadeInUp, staggerContainer } from '../../utils/animations';

// Section component for consistent styling
const Section = ({ 
  icon: Icon, 
  title, 
  children 
}: { 
  icon: React.ElementType; 
  title: string; 
  children: React.ReactNode;
}) => (
  <motion.section 
    className="mb-10"
    variants={fadeInUp}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-(--color-orange-glow) flex items-center justify-center">
        <Icon size={20} className="text-(--color-signal-orange)" />
      </div>
      <h2 className="text-xl font-heading font-semibold text-white">{title}</h2>
    </div>
    <div className="text-(--color-soft-gray) leading-relaxed space-y-4 pl-13">
      {children}
    </div>
  </motion.section>
);

const PrivacyPage = () => {
  const lastUpdated = "December 19, 2025";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-linear-to-b from-(--color-midnight-navy) via-(--color-dark-navy) to-(--color-midnight-navy)" />
        <motion.div 
          className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-(--color-signal-orange)/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div 
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-(--color-orange-glow) mb-6"
              variants={fadeInUp}
            >
              <Shield size={32} className="text-(--color-signal-orange)" />
            </motion.div>
            
            <motion.h1 
              className="text-4xl lg:text-5xl font-heading font-bold text-white mb-4"
              variants={fadeInUp}
            >
              Privacy Policy
            </motion.h1>
            
            <motion.p 
              className="text-lg text-(--color-soft-gray) mb-4"
              variants={fadeInUp}
            >
              Your privacy is important to us. This policy explains how we collect, 
              use, and protect your personal information.
            </motion.p>

            <motion.p 
              className="text-sm text-(--color-muted-gray)"
              variants={fadeInUp}
            >
              Last updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 lg:py-16">
        <div className="container-custom">
          <motion.div 
            className="max-w-4xl mx-auto bg-(--color-charcoal-black)/50 rounded-2xl p-8 lg:p-12 border border-(--color-border-gray)"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {/* Introduction */}
            <Section icon={FileText} title="Introduction">
              <p>
                AmplifyMsg ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard 
                your information when you use our cloud-based messaging and automation platform.
              </p>
              <p>
                By accessing or using AmplifyMsg, you agree to this Privacy Policy. 
                If you do not agree with the terms of this Privacy Policy, please do not 
                access the platform.
              </p>
            </Section>

            {/* Information We Collect */}
            <Section icon={Eye} title="Information We Collect">
              <p className="font-medium text-white">Personal Information</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Name, email address, and phone number</li>
                <li>Company/organization name and details</li>
                <li>Billing information and payment details</li>
                <li>Account credentials and authentication data</li>
                <li>WhatsApp Business API connection details</li>
              </ul>

              <p className="font-medium text-white mt-6">Usage Information</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Log data (IP address, browser type, pages visited)</li>
                <li>Device information (device type, operating system)</li>
                <li>Usage patterns and feature interactions</li>
                <li>Campaign performance metrics and analytics</li>
                <li>Message delivery and engagement data</li>
              </ul>

              <p className="font-medium text-white mt-6">Contact Data</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Contact lists you upload or create</li>
                <li>Message templates and content</li>
                <li>Communication history and records</li>
              </ul>
            </Section>

            {/* How We Use Information */}
            <Section icon={Server} title="How We Use Your Information">
              <p>We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send billing information</li>
                <li>Send you technical notices and support messages</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Detect, investigate, and prevent fraudulent transactions</li>
                <li>Personalize and improve your experience</li>
                <li>Send promotional communications (with your consent)</li>
                <li>Comply with legal obligations and enforce our terms</li>
              </ul>
            </Section>

            {/* Data Sharing */}
            <Section icon={Users} title="Information Sharing">
              <p>We may share your information in the following circumstances:</p>
              
              <p className="font-medium text-white mt-6">Service Providers</p>
              <p>
                We share information with third-party vendors who perform services on our 
                behalf, such as payment processing, data analysis, email delivery, hosting, 
                and customer service.
              </p>

              <p className="font-medium text-white mt-6">Business Transfers</p>
              <p>
                If we are involved in a merger, acquisition, or sale of assets, your 
                information may be transferred as part of that transaction.
              </p>

              <p className="font-medium text-white mt-6">Legal Requirements</p>
              <p>
                We may disclose information if required by law or in response to valid 
                requests by public authorities.
              </p>

              <p className="font-medium text-white mt-6">With Your Consent</p>
              <p>
                We may share information with third parties when you give us explicit 
                consent to do so.
              </p>
            </Section>

            {/* Data Security */}
            <Section icon={Lock} title="Data Security">
              <p>
                We implement appropriate technical and organizational security measures 
                to protect your personal information, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li>End-to-end encryption for data transmission (TLS/SSL)</li>
                <li>Encryption at rest for stored data (AES-256)</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Secure data centers with physical security measures</li>
                <li>Regular backups and disaster recovery procedures</li>
              </ul>
              <p className="mt-4">
                While we strive to protect your information, no method of transmission 
                over the Internet is 100% secure. We cannot guarantee absolute security.
              </p>
            </Section>

            {/* Data Retention */}
            <Section icon={Server} title="Data Retention">
              <p>
                We retain your personal information for as long as your account is active 
                or as needed to provide you services. We may retain certain information 
                as required by law or for legitimate business purposes.
              </p>
              <p>
                When you delete your account, we will delete or anonymize your personal 
                information within 30 days, except for data we are required to retain 
                for legal, regulatory, or legitimate business purposes.
              </p>
            </Section>

            {/* Your Rights */}
            <Section icon={Users} title="Your Rights">
              <p>Depending on your location, you may have the following rights:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Objection:</strong> Object to processing of your personal information</li>
                <li><strong>Restriction:</strong> Request restriction of processing</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us at privacy@amplifymsg.com
              </p>
            </Section>

            {/* Cookies */}
            <Section icon={Eye} title="Cookies and Tracking">
              <p>
                We use cookies and similar tracking technologies to collect information 
                about your browsing activities. You can control cookies through your 
                browser settings.
              </p>
              <p className="font-medium text-white mt-4">Types of cookies we use:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li><strong>Essential cookies:</strong> Required for platform functionality</li>
                <li><strong>Analytics cookies:</strong> Help us understand usage patterns</li>
                <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
              </ul>
            </Section>

            {/* Children's Privacy */}
            <Section icon={AlertCircle} title="Children's Privacy">
              <p>
                AmplifyMsg is not intended for children under 18 years of age. We do not 
                knowingly collect personal information from children. If you are a parent 
                or guardian and believe your child has provided us with personal information, 
                please contact us immediately.
              </p>
            </Section>

            {/* International Transfers */}
            <Section icon={Server} title="International Data Transfers">
              <p>
                Your information may be transferred to and processed in countries other 
                than your own. We ensure appropriate safeguards are in place to protect 
                your information in accordance with this Privacy Policy.
              </p>
            </Section>

            {/* Changes to Policy */}
            <Section icon={FileText} title="Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. We will notify you 
                of any changes by posting the new Privacy Policy on this page and updating 
                the "Last updated" date. We encourage you to review this Privacy Policy 
                periodically.
              </p>
            </Section>

            {/* Contact */}
            <Section icon={Mail} title="Contact Us">
              <p>
                If you have any questions about this Privacy Policy or our data practices, 
                please contact us:
              </p>
              <div className="mt-4 p-4 bg-(--color-navy-light) rounded-xl">
                <p className="font-medium text-white">AmplifyMsg Privacy Team</p>
                <p>Email: privacy@amplifymsg.com</p>
                <p>Address: San Francisco, CA, USA</p>
              </div>
            </Section>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export { PrivacyPage };

